import React, { useEffect, useMemo, useState } from "react";
import LanguageContext from "../helper/LanguageContext";
import { callAPI } from "../helper/apiUtils";
import { $crud } from "../helper/CrudFactory";
import ExportChat from "../pages/Chat/ExportChat";
import { pressProps } from "./chat/info/ChatInfoParts";
import "./chat/info/InfoExportCard.css";
import "./ChatExportMenuItem.css";

const PERIODS = [
    { id: "last_week", labelKey: "export_chat_last_week", fallback: "Last week" },
    { id: "last_month", labelKey: "export_chat_last_month", fallback: "Last month" },
    { id: "last_3_months", labelKey: "export_chat_last_3_months", fallback: "Last 3 months" },
    { id: "custom", labelKey: "export_chat_custom", fallback: "Custom" },
];

function pad(n) {
    return String(n).padStart(2, "0");
}

function toInputDate(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getRangeForPeriod(period, customFrom, customTo) {
    const now = new Date();
    if (period === "last_week") {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now);
        end.setHours(23, 59, 59, 999);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
    }
    if (period === "last_month") {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
    }
    if (period === "last_3_months") {
        const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date(now);
        end.setHours(23, 59, 59, 999);
        return { startDate: start.toISOString(), endDate: end.toISOString() };
    }
    if (period === "custom") {
        if (!customFrom || !customTo) return null;
        const start = new Date(`${customFrom}T00:00:00`);
        const end = new Date(`${customTo}T23:59:59.999`);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
            return null;
        }
        return { startDate: start.toISOString(), endDate: end.toISOString() };
    }
    return null;
}

/**
 * "Export chat group data" row of the info pages' export card (mobile InfoExportPro
 * PdfExportRow): timeframe chips, custom From / To, Export and the staged progress.
 * openSignal > 0 opens it (the info page's Export tile).
 */
export default function ChatExportMenuItem({ chat, isInternal, openSignal = 0 }) {
    const { isGerm, strings } = React.useContext(LanguageContext);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (openSignal) setExpanded(true);
    }, [openSignal]);
    const [period, setPeriod] = useState("");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [phase, setPhase] = useState("idle");
    const [progress, setProgress] = useState(0);
    const [chatMessages, setChatMessages] = useState("");
    const [exportChats, setExportChats] = useState(false);

    const today = useMemo(() => toInputDate(new Date()), []);
    const range = useMemo(() => getRangeForPeriod(period, customFrom, customTo), [period, customFrom, customTo]);
    const canExport = !!range && phase === "idle";
    const isExporting = phase === "fetching" || phase === "generating" || phase === "downloading";

    useEffect(() => {
        if (phase !== "fetching" && phase !== "generating") return undefined;
        const cap = phase === "fetching" ? 42 : 88;
        const step = phase === "fetching" ? 2 : 1;
        const id = setInterval(() => {
            setProgress((current) => Math.min(current + step, cap));
        }, 220);
        return () => clearInterval(id);
    }, [phase]);

    const t = (key, fallback) => strings[key] || fallback;

    const progressLabel = {
        fetching: t("export_chat_progress_fetching", "Fetching messages..."),
        generating: t("export_chat_progress_generating", "Generating PDF..."),
        downloading: t("export_chat_progress_downloading", "Downloading..."),
        done: t("export_chat_progress_done", "Export complete"),
        error: t("export_chat_progress_error", "Export failed"),
    }[phase];

    const resetExport = () => {
        setPhase("idle");
        setProgress(0);
        setExportChats(false);
        setChatMessages("");
    };

    const handleExport = async (event) => {
        event.stopPropagation();
        if (!canExport) {
            if (period === "custom") {
                $crud.notify({
                    type: "error",
                    message: t("export_chat_invalid_range", "Please select a valid date range"),
                });
            }
            return;
        }

        setPhase("fetching");
        setProgress(8);
        setExportChats(false);
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const { data } = await callAPI(
                `/group/${chat?.id}/export`,
                {
                    timezone: timeZone,
                    lang: isGerm ? "de" : "en",
                    viewByOwner: false,
                    startDate: range.startDate,
                    endDate: range.endDate,
                },
                "GET"
            );
            if (!data?.status || !data.messages) {
                setPhase("error");
                setProgress(0);
                $crud.notify({
                    type: "error",
                    message: data?.message || t("export_chat_no_messages", "No messages found in this timeframe"),
                });
                setTimeout(resetExport, 1800);
                return;
            }
            setProgress(55);
            setPhase("generating");
            setChatMessages(data.messages);
            setExportChats(true);
        } catch (error) {
            setPhase("error");
            setProgress(0);
            $crud.notify({
                type: "error",
                message: t("export_chat_progress_error", "Export failed"),
            });
            setTimeout(resetExport, 1800);
        }
    };

    const toggleExpanded = () => {
        if (!isExporting) setExpanded((open) => !open);
    };

    return (
        <div className={`kjp-x-row chat-export-menu-item${expanded ? " is-open" : ""}${isInternal ? " is-internal" : ""}`}>
            <div
                className="kjp-info-row is-pressable"
                {...pressProps(toggleExpanded, isExporting)}
                aria-expanded={expanded}
            >
                <span className="material-symbols-outlined kjp-info-row__icon">picture_as_pdf</span>
                <div className="kjp-info-row__label">
                    {t("export_chat_group", "Export chat group data")}
                </div>
                <span className="material-symbols-outlined kjp-info-row__chevron">
                    {expanded ? "keyboard_arrow_down" : "keyboard_arrow_right"}
                </span>
            </div>
            {expanded && (
                <div className="kjp-x-panel">
                    <div className="kjp-x-caption">
                        {t("export_chat_timeframe", "Timeframe")}
                    </div>
                    <div className="kjp-x-chips" role="radiogroup" aria-label={t("export_chat_timeframe", "Timeframe")}>
                        {PERIODS.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                role="radio"
                                aria-checked={period === item.id}
                                className={`kj-tab kjp-x-chip${period === item.id ? " is-active" : ""}`}
                                disabled={isExporting}
                                onClick={() => setPeriod(item.id)}
                            >
                                {t(item.labelKey, item.fallback)}
                            </button>
                        ))}
                    </div>
                    {period === "custom" && (
                        <div className="kjp-x-dates">
                            <label className="kjp-x-date">
                                <div className="kjp-x-label">{t("export_chat_from", "From")}</div>
                                <input
                                    type="date"
                                    value={customFrom}
                                    max={customTo || today}
                                    disabled={isExporting}
                                    className={`kjp-x-input${!customFrom ? " is-incomplete" : ""}`}
                                    onChange={(event) => setCustomFrom(event.target.value)}
                                />
                            </label>
                            <label className="kjp-x-date">
                                <div className="kjp-x-label">{t("export_chat_to", "To")}</div>
                                <input
                                    type="date"
                                    value={customTo}
                                    min={customFrom || undefined}
                                    max={today}
                                    disabled={isExporting}
                                    className={`kjp-x-input${!customTo ? " is-incomplete" : ""}`}
                                    onChange={(event) => setCustomTo(event.target.value)}
                                />
                            </label>
                        </div>
                    )}
                    <div className="kjp-x-actions">
                        <button
                            type="button"
                            className={`kj-btn kj-btn--primary${isExporting ? " kj-btn--loading" : ""}`}
                            onClick={handleExport}
                            disabled={!canExport && !isExporting}
                            aria-busy={isExporting}
                        >
                            <span className="material-symbols-outlined">download</span>
                            {t("export_chat_start", "Export")}
                        </button>
                    </div>
                    {phase !== "idle" && (
                        <div className={`kjp-x-progress${phase === "error" ? " is-error" : ""}`} role="status" aria-live="polite">
                            <div className="kjp-x-progress__copy">
                                <div>{progressLabel}</div>
                                <div>{Math.round(progress)}%</div>
                            </div>
                            <div
                                className="kjp-x-progress__track"
                                role="progressbar"
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={Math.round(progress)}
                            >
                                <div
                                    className="kjp-x-progress__fill"
                                    style={{ '--kjp-x-progress': `${Math.max(progress, phase === "error" ? 100 : 0)}%` }}
                                />
                            </div>
                        </div>
                    )}
                    {exportChats && (
                        <ExportChat
                            chat={chat}
                            chatMessages={chatMessages}
                            hide={resetExport}
                            onProgress={(value, nextPhase) => {
                                setProgress(value);
                                if (nextPhase) setPhase(nextPhase);
                            }}
                            onComplete={() => {
                                setPhase("done");
                                setProgress(100);
                                setTimeout(resetExport, 2200);
                            }}
                            onError={() => {
                                setPhase("error");
                                setProgress(0);
                                $crud.notify({
                                    type: "error",
                                    message: t("export_chat_progress_error", "Export failed"),
                                });
                                setTimeout(resetExport, 1800);
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
